import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { CustomProperty } from '../../model/custom-property';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { ArticleUnitKindToString, ArticleUnit, CustomPropertyDataType, CustomPropertyTypeToString } from '../../model/const/enums';

@Component({
    selector: 'app-custom-property-form',
    templateUrl: './custom-property-form.component.html',
    styleUrls: ['./custom-property-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: CustomPropertyService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomPropertyFormComponent extends FormComponentBase<CustomProperty> {
  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    service: CustomPropertyService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  units = [
    { name: ArticleUnitKindToString(ArticleUnit.None), value: ArticleUnit.None },
    { name: ArticleUnitKindToString(ArticleUnit.Units), value: ArticleUnit.Units },
    { name: ArticleUnitKindToString(ArticleUnit.Kg), value: ArticleUnit.Kg },
    { name: ArticleUnitKindToString(ArticleUnit.Gram), value: ArticleUnit.Gram },
    { name: ArticleUnitKindToString(ArticleUnit.Kwh), value: ArticleUnit.Kwh },
    { name: ArticleUnitKindToString(ArticleUnit.Ton), value: ArticleUnit.Ton },
    { name: ArticleUnitKindToString(ArticleUnit.Km), value: ArticleUnit.Km },
    { name: ArticleUnitKindToString(ArticleUnit.M2), value: ArticleUnit.M2 },
    { name: ArticleUnitKindToString(ArticleUnit.M3), value: ArticleUnit.M3 },
    { name: ArticleUnitKindToString(ArticleUnit.Litre), value: ArticleUnit.Litre },
  ];

  dataTypes = [
    { name: CustomPropertyTypeToString(CustomPropertyDataType.Boolean), value: CustomPropertyDataType.Boolean },
    { name: CustomPropertyTypeToString(CustomPropertyDataType.Date), value: CustomPropertyDataType.Date },
    { name: CustomPropertyTypeToString(CustomPropertyDataType.Numeric), value: CustomPropertyDataType.Numeric },
    { name: CustomPropertyTypeToString(CustomPropertyDataType.Selection), value: CustomPropertyDataType.Selection },
    { name: CustomPropertyTypeToString(CustomPropertyDataType.Text), value: CustomPropertyDataType.Text },
  ];

  decimals = [
    { name: '0', value: '0' },
    { name: '1', value: '1' },
    { name: '2', value: '2' },
    { name: '3', value: '3' },
    { name: '4', value: '4' },
    { name: '5', value: '5' },
    { name: '6', value: '6' },
  ];

  protected createModel(): CustomProperty {
    return new CustomProperty();
  }

  protected getFormGroup(): FormGroup {
    return this._fb.group({
      caption: this._fc.createWithOptions('CUSTOM_PROPERTY.CAPTION', this.dataContext.caption, { length: 30, required: true }),
      unit: this._fc.create('ARTICLE.UNIT', this.dataContext.unit),
      dataType: this._fc.create('CUSTOM_PROPERTY.DATATYPECAPTION', this.dataContext.dataType),
      minValue: this._fc.create('CUSTOM_PROPERTY.MINVALUE', this.dataContext.minValue),
      maxValue: this._fc.create('CUSTOM_PROPERTY.MAXVALUE', this.dataContext.maxValue),
      decimalCount: this._fc.create('CUSTOM_PROPERTY.DECIMALS', this.dataContext.decimalCount.toString()),
      selectableValues: this._fc.create('CUSTOM_PROPERTY.SELECTION_HEADER', this.dataContext.selectableValues),
      availableOnCompany: this._fc.create('CUSTOM_PROPERTY.AVAILABLE_ON_COMPANY', this.dataContext.availableOnCompany),
      availableOnDwelling: this._fc.create('CUSTOM_PROPERTY.AVAILABLE_ON_DWELLING', this.dataContext.availableOnDwelling),
      availableOnProperty: this._fc.create('CUSTOM_PROPERTY.AVAILABLE_ON_PROPERTY', this.dataContext.availableOnProperty),
      availableOnBuilding: this._fc.create('CUSTOM_PROPERTY.AVAILABLE_ON_BUILDING', this.dataContext.availableOnBuilding),
      availableOnPremises: this._fc.create('CUSTOM_PROPERTY.AVAILABLE_ON_PREMISES', this.dataContext.availableOnPremises),
    });
  }

  private get dataType(): CustomPropertyDataType {
    return this.form.controls['dataType'].value;
  }

  public get showNumeric(): boolean {
    return this.dataType === CustomPropertyDataType.Numeric;
  }

  public get showSelection(): boolean {
    return this.dataType === CustomPropertyDataType.Selection;
  }
}
